import { getContract } from "config/contracts";
import { getKyberSwapUrl, KYBER_SWAP_CLIENT_ID, EXCLUDED_KYBER_SWAP_SOURCES } from "config/externalSwaps";
import { USD_DECIMALS } from "config/factors";
import { buildUrl } from "lib/buildUrl";
import { metrics } from "lib/metrics";
import { expandDecimals, numberToBigint } from "lib/numbers";
import type { ContractsChainId } from "sdk/configs/chains";
import { convertTokenAddress, getToken } from "sdk/configs/tokens";

import { isAbortError } from "./utils";

type KyberSwapRoutesResponse = {
  code: number;
  message: string;
  data?: {
    routeSummary: KyberSwapRouteSummary;
    routerAddress: string;
  };
};

type KyberSwapRouteSummary = {
  tokenIn: string;
  amountIn: string;
  amountInUsd: string;
  tokenOut: string;
  amountOut: string;
  amountOutUsd: string;
  gas: string;
  gasPrice: string;
  gasUsd: string;
  extraFee: {
    feeAmount: string;
    chargeFeeBy: string;
    isInBps: boolean;
    feeReceiver: string;
  };
  route: unknown[][];
};

type KyberSwapBuildResponse = {
  code: number;
  message: string;
  data?: {
    amountIn: string;
    amountInUsd: string;
    amountOut: string;
    amountOutUsd: string;
    gas: string;
    gasUsd: string;
    outputChange: {
      amount: string;
      percent: number;
      level: number;
    };
    data: string;
    routerAddress: string;
  };
};

export type KyberSwapQuote = {
  to: string;
  data: string;
  value: bigint;
  estimatedGas: bigint;
  usdIn: bigint;
  usdOut: bigint;
  priceIn: bigint;
  priceOut: bigint;
  gasPrice: bigint;
  amountIn: bigint;
  outputAmount: bigint;
  slippage: number;
};

const KYBER_SWAP_SLIPPAGE_ERROR_CODE = 4222;

export class KyberSwapSlippageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "KyberSwapSlippageError";
  }
}

export async function getKyberSwapTxnData({
  chainId,
  tokenInAddress,
  tokenOutAddress,
  amountIn,
  senderAddress,
  receiverAddress,
  gasPrice,
  slippage,
  signal,
}: {
  senderAddress: string;
  receiverAddress: string;
  chainId: ContractsChainId;
  tokenInAddress: string;
  tokenOutAddress: string;
  amountIn: bigint;
  gasPrice: bigint;
  slippage: number;
  signal?: AbortSignal;
}): Promise<KyberSwapQuote | undefined> {
  const baseUrl = getKyberSwapUrl(chainId);
  const tokenIn = getToken(chainId, tokenInAddress);
  const tokenOut = getToken(chainId, tokenOutAddress);

  const wrappedTokenIn = convertTokenAddress(chainId, tokenInAddress, "wrapped");
  const wrappedTokenOut = convertTokenAddress(chainId, tokenOutAddress, "wrapped");

  const excludedSources = EXCLUDED_KYBER_SWAP_SOURCES[chainId]?.join(",");

  // Step 1: Get route
  const routeUrl = buildUrl(baseUrl, "/api/v1/routes", {
    tokenIn: wrappedTokenIn,
    tokenOut: wrappedTokenOut,
    amountIn: amountIn.toString(),
    gasPrice: gasPrice.toString(),
    excludedSources,
  });

  try {
    const routeRes = await fetch(routeUrl, {
      headers: { "x-client-id": KYBER_SWAP_CLIENT_ID },
      signal,
    });

    if (routeRes.status === 403) {
      throw new Error(`IP is banned ${await routeRes.text()}`);
    }

    const routeData = (await routeRes.json()) as KyberSwapRoutesResponse;

    if (!routeData.data?.routeSummary || routeData.code !== 0) {
      throw new Error(`Failed to get route: ${routeData.code} ${routeData.message}`);
    }

    const { routeSummary, routerAddress } = routeData.data;

    if (routerAddress !== getContract(chainId, "KyberSwapRouter")) {
      throw new Error(`Invalid KyberSwapRouter address: ${routerAddress}`);
    }

    // Step 2: Build transaction
    const buildUrl_ = buildUrl(baseUrl, "/api/v1/route/build");

    const buildRes = await fetch(buildUrl_, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": KYBER_SWAP_CLIENT_ID,
      },
      body: JSON.stringify({
        routeSummary,
        sender: senderAddress,
        recipient: receiverAddress,
        slippageTolerance: slippage,
      }),
      signal,
    });

    const buildData = (await buildRes.json()) as KyberSwapBuildResponse;

    if (!buildData.data || buildData.code !== 0) {
      if (buildData.code === KYBER_SWAP_SLIPPAGE_ERROR_CODE) {
        throw new KyberSwapSlippageError(`${buildData.message}`);
      }
      throw new Error(`Failed to build transaction: ${buildData.code} ${buildData.message}`);
    }

    if (buildData.data.routerAddress !== getContract(chainId, "KyberSwapRouter")) {
      throw new Error(`Invalid KyberSwapRouter address in build response: ${buildData.data.routerAddress}`);
    }

    const amountInBigint = BigInt(routeSummary.amountIn);
    const amountOutBigint = BigInt(buildData.data.amountOut);
    const usdIn = numberToBigint(parseFloat(buildData.data.amountInUsd), USD_DECIMALS);
    const usdOut = numberToBigint(parseFloat(buildData.data.amountOutUsd), USD_DECIMALS);

    const priceIn = calcTokenPrice(amountInBigint, buildData.data.amountInUsd, tokenIn.decimals);
    const priceOut = calcTokenPrice(amountOutBigint, buildData.data.amountOutUsd, tokenOut.decimals);

    return {
      to: buildData.data.routerAddress,
      data: buildData.data.data,
      value: 0n,
      estimatedGas: BigInt(buildData.data.gas),
      usdIn,
      usdOut,
      priceIn,
      priceOut,
      gasPrice,
      amountIn: amountInBigint,
      outputAmount: amountOutBigint,
      slippage,
    };
  } catch (e) {
    if (e instanceof KyberSwapSlippageError) {
      throw e;
    }
    if (isAbortError(e)) {
      throw e;
    }
    e.message += ` URL: ${routeUrl.replace(receiverAddress, "...")}`;
    metrics.pushError(e, "externalSwap.getKyberSwapTxnData");
    return undefined;
  }
}

export type KyberSwapRouteResult = {
  routeSummary: KyberSwapRouteSummary;
  routerAddress: string;
  outputAmount: bigint;
  amountIn: bigint;
};

export async function getKyberSwapRoute({
  chainId,
  tokenInAddress,
  tokenOutAddress,
  amountIn,
  gasPrice,
  signal,
}: {
  chainId: ContractsChainId;
  tokenInAddress: string;
  tokenOutAddress: string;
  amountIn: bigint;
  gasPrice: bigint;
  signal?: AbortSignal;
}): Promise<KyberSwapRouteResult | undefined> {
  const baseUrl = getKyberSwapUrl(chainId);

  const wrappedTokenIn = convertTokenAddress(chainId, tokenInAddress, "wrapped");
  const wrappedTokenOut = convertTokenAddress(chainId, tokenOutAddress, "wrapped");

  const excludedSources = EXCLUDED_KYBER_SWAP_SOURCES[chainId]?.join(",");

  const routeUrl = buildUrl(baseUrl, "/api/v1/routes", {
    tokenIn: wrappedTokenIn,
    tokenOut: wrappedTokenOut,
    amountIn: amountIn.toString(),
    gasPrice: gasPrice.toString(),
    excludedSources,
  });

  try {
    const routeRes = await fetch(routeUrl, {
      headers: { "x-client-id": KYBER_SWAP_CLIENT_ID },
      signal,
    });

    if (routeRes.status === 403) {
      throw new Error(`IP is banned ${await routeRes.text()}`);
    }

    const routeData = (await routeRes.json()) as KyberSwapRoutesResponse;

    if (!routeData.data?.routeSummary || routeData.code !== 0) {
      return undefined;
    }

    const { routeSummary, routerAddress } = routeData.data;

    if (routerAddress !== getContract(chainId, "KyberSwapRouter")) {
      return undefined;
    }

    return {
      routeSummary,
      routerAddress,
      outputAmount: BigInt(routeSummary.amountOut),
      amountIn: BigInt(routeSummary.amountIn),
    };
  } catch (e) {
    if (isAbortError(e)) {
      throw e;
    }
    metrics.pushError(e, "externalSwap.getKyberSwapRoute");
    return undefined;
  }
}

export async function getKyberSwapBuildFromRoute({
  chainId,
  routeSummary,
  senderAddress,
  receiverAddress,
  slippage,
  tokenInAddress,
  tokenOutAddress,
  gasPrice,
  signal,
}: {
  chainId: ContractsChainId;
  routeSummary: KyberSwapRouteSummary;
  senderAddress: string;
  receiverAddress: string;
  slippage: number;
  tokenInAddress: string;
  tokenOutAddress: string;
  gasPrice: bigint;
  signal?: AbortSignal;
}): Promise<KyberSwapQuote | undefined> {
  const baseUrl = getKyberSwapUrl(chainId);
  const tokenIn = getToken(chainId, tokenInAddress);
  const tokenOut = getToken(chainId, tokenOutAddress);

  try {
    const buildUrl_ = buildUrl(baseUrl, "/api/v1/route/build");

    const buildRes = await fetch(buildUrl_, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": KYBER_SWAP_CLIENT_ID,
      },
      body: JSON.stringify({
        routeSummary,
        sender: senderAddress,
        recipient: receiverAddress,
        slippageTolerance: slippage,
      }),
      signal,
    });

    const buildData = (await buildRes.json()) as KyberSwapBuildResponse;

    if (!buildData.data || buildData.code !== 0) {
      return undefined;
    }

    if (buildData.data.routerAddress !== getContract(chainId, "KyberSwapRouter")) {
      return undefined;
    }

    const amountInBigint = BigInt(routeSummary.amountIn);
    const amountOutBigint = BigInt(buildData.data.amountOut);
    const usdIn = numberToBigint(parseFloat(buildData.data.amountInUsd), USD_DECIMALS);
    const usdOut = numberToBigint(parseFloat(buildData.data.amountOutUsd), USD_DECIMALS);

    const priceIn = calcTokenPrice(amountInBigint, buildData.data.amountInUsd, tokenIn.decimals);
    const priceOut = calcTokenPrice(amountOutBigint, buildData.data.amountOutUsd, tokenOut.decimals);

    return {
      to: buildData.data.routerAddress,
      data: buildData.data.data,
      value: 0n,
      estimatedGas: BigInt(buildData.data.gas),
      usdIn,
      usdOut,
      priceIn,
      priceOut,
      gasPrice,
      amountIn: amountInBigint,
      outputAmount: amountOutBigint,
      slippage,
    };
  } catch (e) {
    if (isAbortError(e)) {
      throw e;
    }
    metrics.pushError(e, "externalSwap.getKyberSwapBuildFromRoute");
    return undefined;
  }
}

export function calcTokenPrice(amount: bigint, usdValue: string, decimals: number): bigint {
  if (amount <= 0n) return 0n;

  const usdBigint = numberToBigint(parseFloat(usdValue), USD_DECIMALS);
  return (usdBigint * expandDecimals(1n, decimals)) / amount;
}
